import React from 'react';
import { Typography, TypographyProps } from '@mui/material';

/**
 * Money cell for statutory statements (Balance Sheet, P&L, Cash Flow, Trial
 * Balance).
 *
 * Distinct from `IndianAmountDisplay` on two points that matter specifically
 * for statement bodies:
 *  - `null`/`undefined` render as an em dash, never as "0.00". A missing
 *    prior-period figure is not a zero.
 *  - Negative amounts use the accounting parenthesis convention, `(1,234.56)`,
 *    so the sign never depends on colour alone.
 */

export interface StatementAmountProps {
  /** null/undefined render as an em dash. */
  value: number | null | undefined;
  bold?: boolean;
  /** 'auto' colours by sign; 'neutral' keeps statement lines a single ink colour. */
  tone?: 'positive' | 'negative' | 'neutral' | 'auto';
  variant?: TypographyProps['variant'];
  suffix?: string;
}

const StatementAmount: React.FC<StatementAmountProps> = ({
  value,
  bold = false,
  tone = 'neutral',
  variant,
  suffix,
}) => {
  if (value === null || value === undefined) {
    return (
      <Typography
        component="span"
        variant={variant}
        sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.disabled' }}
      >
        —
      </Typography>
    );
  }

  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  const display = value < 0 ? `(${formatted})` : formatted;

  const color =
    tone === 'auto'
      ? value < 0
        ? 'error.main'
        : value > 0
        ? 'success.main'
        : 'text.primary'
      : tone === 'positive'
      ? 'success.main'
      : tone === 'negative'
      ? 'error.main'
      : 'text.primary';

  return (
    <Typography
      component="span"
      variant={variant}
      sx={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? 700 : 400,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {display}
      {suffix ? ` ${suffix}` : ''}
    </Typography>
  );
};

export default StatementAmount;
